import { useEffect, useRef, useImperativeHandle, forwardRef, useState, useCallback } from 'react';
import Hls, { Events, ErrorData, ErrorTypes } from 'hls.js';

export interface HLSPlayerProps {
  src: string;
  vodSignatureParams?: string;
  liveSignatureParams?: string;
  autoPlay?: boolean;
  onBuffering?: (isBuffering: boolean) => void;
  onError?: (message: string) => void;
  onModeChange?: (mode: 'LIVE' | 'VOD' | 'AD') => void;
}

export interface HLSPlayerHandle {
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  setVolume: (volume: number) => void;
  getVolume: () => number;
  isMuted: () => boolean;
  toggleMute: () => void;
  isPlaying: () => boolean;
  getVideoElement: () => HTMLVideoElement | null;
  /**
   * Set the quality level for hls.js playback.
   * - Pass -1 to enable ABR (Auto mode).
   * - Pass a specific level index (0-based) to lock to that rendition and disable ABR.
   */
  setQualityLevel: (levelIndex: number) => void;
}

/**
 * Extract query string from a URL (everything after '?').
 */
function extractQueryParams(url: string): string {
  const idx = url.indexOf('?');
  return idx >= 0 ? url.substring(idx + 1) : '';
}

/**
 * HLS Player component wrapping hls.js for CMAF HLS playback.
 *
 * Handles:
 * - HLS.js initialization and lifecycle
 * - Error detection and recovery
 * - Buffering state reporting
 * - Native HLS fallback for Safari
 * - CloudFront signed URL injection for channel/* and vod/* requests
 */
export const HLSPlayer = forwardRef<HLSPlayerHandle, HLSPlayerProps>(
  ({ src, vodSignatureParams = '', liveSignatureParams = '', autoPlay = true, onBuffering, onError, onModeChange }, ref) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const hlsRef = useRef<Hls | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);

    // Keep a ref to vodSignatureParams so xhrSetup always uses the latest value
    const vodSignatureParamsRef = useRef(vodSignatureParams);
    useEffect(() => {
      vodSignatureParamsRef.current = vodSignatureParams;
    }, [vodSignatureParams]);

    // Keep a ref to liveSignatureParams so xhrSetup always uses the latest value
    const liveSignatureParamsRef = useRef(liveSignatureParams);
    useEffect(() => {
      liveSignatureParamsRef.current = liveSignatureParams;
    }, [liveSignatureParams]);

    // Keep a ref to the src (channelUrl) for extracting channel signature params
    const srcRef = useRef(src);
    useEffect(() => {
      srcRef.current = src;
    }, [src]);

    const handleError = useCallback(
      (_event: typeof Events.ERROR, data: ErrorData) => {
        if (data.fatal) {
          switch (data.type) {
            case ErrorTypes.NETWORK_ERROR:
              // Try to recover from network error
              hlsRef.current?.startLoad();
              onError?.('networkError');
              break;
            case ErrorTypes.MEDIA_ERROR:
              hlsRef.current?.recoverMediaError();
              onError?.('mediaError');
              break;
            default:
              // Fatal error that cannot be recovered
              hlsRef.current?.destroy();
              onError?.('fatalError');
              break;
          }
        }
      },
      [onError]
    );

    // Keep refs for callbacks used inside the hls.js effect
    const handleErrorRef = useRef(handleError);
    useEffect(() => { handleErrorRef.current = handleError; }, [handleError]);

    const onModeChangeRef = useRef(onModeChange);
    useEffect(() => { onModeChangeRef.current = onModeChange; }, [onModeChange]);

    useEffect(() => {
      const video = videoRef.current;
      if (!video || !src) return;

      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          backBufferLength: 30,
          maxBufferLength: 10,
          maxMaxBufferLength: 30,
          liveSyncDurationCount: 3,
          liveMaxLatencyDurationCount: 5,
          xhrSetup: (xhr: XMLHttpRequest, url: string) => {
            let finalUrl = url;

            if (url.includes('/channel/') && !url.includes('Policy=')) {
              // Append channel signature from the playback URL
              const params = extractQueryParams(srcRef.current);
              if (params) {
                finalUrl = url + (url.includes('?') ? '&' : '?') + params;
              }
            } else if (url.includes('/vod/') && !url.includes('Policy=')) {
              // Append VOD signature (refreshed every 2min)
              const vodParams = vodSignatureParamsRef.current;
              if (vodParams) {
                finalUrl = url + (url.includes('?') ? '&' : '?') + vodParams;
              }
            } else if (url.includes('/live/') && !url.includes('Policy=')) {
              // Append live signature (refreshed every 2min)
              const liveParams = liveSignatureParamsRef.current;
              if (liveParams) {
                finalUrl = url + (url.includes('?') ? '&' : '?') + liveParams;
              }
            }

            if (finalUrl !== url) {
              xhr.open('GET', finalUrl, true);
            }
          },
        });

        hls.loadSource(src);
        hls.attachMedia(video);

        hls.on(Events.MANIFEST_PARSED, () => {
          if (autoPlay) {
            video.play().catch(() => {
              // Autoplay blocked — user interaction required
            });
          }
        });

        hls.on(Events.ERROR, (_event: typeof Events.ERROR, data: ErrorData) => {
          handleErrorRef.current(_event, data);
        });

        // Detect live vs VOD
        hls.on(Events.LEVEL_LOADED, (_event, data) => {
          const isLive = data.details.live;
          onModeChangeRef.current?.(isLive ? 'LIVE' : 'VOD');
        });

        hlsRef.current = hls;

        return () => {
          hls.destroy();
          hlsRef.current = null;
        };
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Native HLS support (Safari)
        video.src = src;
        if (autoPlay) {
          video.play().catch(() => {});
        }
      } else {
        onError?.('unsupported');
      }
    }, [src, autoPlay]);  // Only recreate hls.js when source changes

    // Buffering detection
    useEffect(() => {
      const video = videoRef.current;
      if (!video) return;

      const handleWaiting = () => onBuffering?.(true);
      const handlePlaying = () => {
        onBuffering?.(false);
        setIsPlaying(true);
      };
      const handlePause = () => setIsPlaying(false);
      const handlePlay = () => setIsPlaying(true);

      video.addEventListener('waiting', handleWaiting);
      video.addEventListener('playing', handlePlaying);
      video.addEventListener('pause', handlePause);
      video.addEventListener('play', handlePlay);

      return () => {
        video.removeEventListener('waiting', handleWaiting);
        video.removeEventListener('playing', handlePlaying);
        video.removeEventListener('pause', handlePause);
        video.removeEventListener('play', handlePlay);
      };
    }, [onBuffering]);

    useImperativeHandle(ref, () => ({
      play: () => {
        videoRef.current?.play();
      },
      pause: () => {
        videoRef.current?.pause();
      },
      togglePlay: () => {
        const video = videoRef.current;
        if (!video) return;
        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
      },
      setVolume: (volume: number) => {
        if (videoRef.current) {
          videoRef.current.volume = Math.max(0, Math.min(1, volume));
        }
      },
      getVolume: () => videoRef.current?.volume ?? 1,
      isMuted: () => videoRef.current?.muted ?? false,
      toggleMute: () => {
        if (videoRef.current) {
          videoRef.current.muted = !videoRef.current.muted;
        }
      },
      isPlaying: () => isPlaying,
      getVideoElement: () => videoRef.current,
      setQualityLevel: (levelIndex: number) => {
        const hls = hlsRef.current;
        if (!hls) return;
        // -1 enables ABR (Auto), >= 0 locks to a specific rendition level
        hls.currentLevel = levelIndex;
      },
    }));

    return (
      <video
        ref={videoRef}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          backgroundColor: '#000',
        }}
        playsInline
        aria-label="Lecteur vidéo Capoeira Sempre Senzala"
      />
    );
  }
);

HLSPlayer.displayName = 'HLSPlayer';
